import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { SavedWhiteboard, SoloWhiteboardSnapshot, SoloWhiteboardCommandAck, SoloWhiteboardTarget, WhiteboardAction, WhiteboardSnapshot } from "@calega/realtime";
import { api, ApiError } from "../lib/api";
import { useRealtime } from "../realtime/RealtimeContext";
import { BoardContext, useWhiteboard, type BoardContextValue } from "./WhiteboardContext";

interface SoloControls {
  visible: boolean; workspace: SoloWhiteboardSnapshot | null; notice: string;
  start(target: SoloWhiteboardTarget): Promise<void>; restore(): Promise<void>; reset(): void;
  transition(work: () => Promise<unknown>): Promise<boolean>;
}
const SoloContext = createContext<SoloControls | null>(null);
const scopeOf = (value: SoloWhiteboardSnapshot) => ({ generation: value.board.generation, revision: value.board.revision });
export function SoloWhiteboardProvider({ children }: { children: ReactNode }) {
  const sync = useWhiteboard(), realtime = useRealtime();
  const [workspace, setWorkspace] = useState<SoloWhiteboardSnapshot | null>(null), current = useRef(workspace);
  const [visible, setVisible] = useState(false), [ready, setReady] = useState(false), [busy, setBusy] = useState(false), [notice, setNotice] = useState("");
  const [saved, setSaved] = useState<SavedWhiteboard[]>([]), [personal, setPersonal] = useState<SavedWhiteboard[]>([]), [ending, setEnding] = useState(false);
  const epoch = useRef(0), offset = useRef(0), editorId = useRef(crypto.randomUUID()), pending = useRef(0);
  const pendingSave = useRef<{ signature: string; newId: string } | null>(null);
  const continuation = useRef<{ work: () => Promise<unknown>; resolve: (value: boolean) => void } | null>(null);
  function receive(next: SoloWhiteboardSnapshot, previous = current.current) {
    if (previous && current.current && previous.id === current.current.id && previous.board.generation !== current.current.board.generation) return;
    if (current.current?.id === next.id && current.current.board.generation === next.board.generation && current.current.board.revision > next.board.revision) return;
    offset.current = next.serverNow - Date.now(); current.current = next; setWorkspace(next); setReady(true);
  }
  function reset() {
    epoch.current++; current.current = null; setWorkspace(null); setVisible(false); setReady(false); setBusy(false); setSaved([]); setPersonal([]); setEnding(false); pending.current = 0; pendingSave.current = null;
    const action = continuation.current; continuation.current = null; action?.resolve(false);
  }
  function failure(error: unknown) {
    setReady(false); setNotice((error as Error).message);
    if (error instanceof ApiError && [401, 403, 404].includes(error.status)) reset();
  }
  async function restore() {
    const token = epoch.current;
    try {
      const { workspace: next } = await api<{ workspace: SoloWhiteboardSnapshot | null }>("/whiteboards/workspaces", { cache: "no-store", signal: AbortSignal.timeout(10000) });
      if (token !== epoch.current) return;
      if (next) receive(next); else { current.current = null; setWorkspace(null); setReady(false); }
    } catch (error) { if (token === epoch.current) failure(error); }
  }
  async function start(target: SoloWhiteboardTarget) {
    if (realtime.session) throw new Error("Bitte die Sync-Sitzung zuerst regulär beenden.");
    const token = epoch.current;
    setBusy(true); setNotice("");
    try {
      const existing = await api<{ workspace: SoloWhiteboardSnapshot | null }>("/whiteboards/workspaces", { cache: "no-store" });
      const next = existing.workspace ?? await api<SoloWhiteboardSnapshot>("/whiteboards/workspaces", { method: "POST", body: JSON.stringify(target) });
      if (token === epoch.current) { receive(next); setVisible(true); }
    } catch (error) { if (token === epoch.current) failure(error); throw error; }
    finally { if (token === epoch.current) setBusy(false); }
  }
  async function refresh() {
    const state = current.current, token = epoch.current; if (!state) return;
    try {
      const next = await api<SoloWhiteboardSnapshot>(`/whiteboards/workspaces/${state.id}`, { cache: "no-store", signal: AbortSignal.timeout(10000) });
      if (token === epoch.current && current.current?.id === state.id) receive(next, state);
    } catch (error) { if (token === epoch.current) failure(error); }
  }
  useEffect(() => {
    if (!workspace) return;
    const check = () => { if (document.visibilityState === "visible") void refresh(); };
    const timer = window.setInterval(check, 5000); window.addEventListener("focus", check);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", check); };
  }, [workspace?.id]);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => { if (current.current?.board.dirty) event.preventDefault(); };
    window.addEventListener("beforeunload", warn); return () => window.removeEventListener("beforeunload", warn);
  }, []);
  async function command(action: WhiteboardAction, scope?: Pick<WhiteboardSnapshot, "generation" | "permissionRevision">) {
    const state = current.current, token = epoch.current;
    if (!state || !ready) throw new Error("Der Solo-Tisch wird noch abgeglichen.");
    const input = { commandId: crypto.randomUUID(), editorId: editorId.current, generation: scope?.generation ?? state.board.generation,
      permissionRevision: scope?.permissionRevision ?? state.board.permissionRevision, baseRevision: state.board.revision, action };
    pending.current++; setBusy(true);
    try {
      let next!: SoloWhiteboardSnapshot | SoloWhiteboardCommandAck;
      for (let attempt = 0; attempt < 2; attempt++) {
        try { next = await api<SoloWhiteboardSnapshot | SoloWhiteboardCommandAck>(`/whiteboards/workspaces/${state.id}/commands`, { method: "POST", body: JSON.stringify(input), signal: AbortSignal.timeout(10000) }); break; }
        catch (error) { if (attempt || error instanceof ApiError && error.status < 500) throw error; }
      }
      if (token === epoch.current && current.current?.id === state.id) {
        if ("board" in next) receive(next, state);
        else if (next.generation !== current.current.board.generation || next.revision !== current.current.board.revision) await refresh();
        setNotice("");
      }
    } catch (error) { if (token === epoch.current) { failure(error); void refresh(); } throw error; }
    finally { if (token === epoch.current) { pending.current--; setBusy(pending.current > 0); } }
  }
  async function list() {
    const state = current.current, token = epoch.current; if (!state) return;
    const rows = await api<SavedWhiteboard[]>(`/whiteboards/workspaces/${state.id}/saved`, { cache: "no-store" });
    if (token === epoch.current && current.current?.id === state.id) setSaved(rows);
  }
  async function save(title: string, asNew = false) {
    const state = current.current, token = epoch.current; if (!state || !ready) throw new Error("Bitte den Tisch zuerst abgleichen.");
    const signature = JSON.stringify([state.id, state.board.generation, state.board.revision, title, asNew]);
    if (pendingSave.current?.signature !== signature) pendingSave.current = { signature, newId: crypto.randomUUID() };
    setBusy(true);
    try {
      await api(state.target.kind === "personal" ? "/whiteboards/personal" : `/whiteboards/workspaces/${state.id}/saved`, { method: "POST", body: JSON.stringify({ ...scopeOf(state), title, ...(state.target.kind === "personal" ? { workspaceId: state.id } : {}),
        ...(!asNew && state.board.saved ? { id: state.board.saved.id, savedRevision: state.board.saved.revision } : { newId: pendingSave.current.newId }) }) });
      if (token !== epoch.current) return;
      pendingSave.current = null; await refresh(); await list(); setNotice("Whiteboard gespeichert.");
    } catch (error) { if (token === epoch.current) { failure(error); void refresh(); } throw error; }
    finally { if (token === epoch.current) setBusy(false); }
  }
  async function load(id: string, personalSource = false) {
    const state = current.current, token = epoch.current; if (!state) return;
    setBusy(true);
    try {
      const next = await api<SoloWhiteboardSnapshot>(`/whiteboards/workspaces/${state.id}/${personalSource ? "personal" : "saved"}/${id}/load`, { method: "POST", body: JSON.stringify(scopeOf(state)) });
      if (token === epoch.current && current.current?.id === state.id) { receive(next, state); setNotice(""); }
    } catch (error) { if (token === epoch.current) { failure(error); void refresh(); } throw error; }
    finally { if (token === epoch.current) setBusy(false); }
  }
  async function discard() {
    const state = current.current; if (!state) return;
    try { await api(`/whiteboards/workspaces/${state.id}/discard`, { method: "POST", body: JSON.stringify(scopeOf(state)) }); }
    catch (error) { if (!(error instanceof ApiError) || error.status !== 404) throw error; }
    reset();
  }
  async function transition(work: () => Promise<unknown>): Promise<boolean> {
    if (continuation.current) return false;
    if (current.current?.board.dirty) return new Promise((resolve) => { continuation.current = { work, resolve }; setEnding(true); setVisible(true); });
    try { if (current.current) await discard(); await work(); return true; }
    catch (error) { failure(error); throw error; }
  }
  async function finishEnd(store: boolean, title: string) {
    if (store) { await save(title); if (current.current?.board.dirty) throw new Error("Der Tisch wurde während des Speicherns verändert. Bitte erneut speichern."); }
    const action = continuation.current; if (!action) return;
    continuation.current = null;
    let discarded = false;
    try { await discard(); discarded = true; await action.work(); action.resolve(true); }
    catch (error) {
      if (discarded || error instanceof ApiError && [401, 403, 404].includes(error.status)) { action.resolve(false); failure(error); }
      else { continuation.current = action; failure(error); void refresh(); }
      throw error;
    }
  }
  async function listPersonal() {
    const token = epoch.current;
    const rows = await api<SavedWhiteboard[]>("/whiteboards/personal", { cache: "no-store" });
    if (token === epoch.current) setPersonal(rows);
  }
  const soloActive = Boolean(workspace && (!realtime.session || ending));
  const value: BoardContextValue = soloActive ? {
    snapshot: workspace!.board, solo: workspace!, soloVisible: visible, ready, busy, notice, previews: [], saved, ending, command, preview: () => undefined,
    refresh, list, save, load, removeSaved: async (board) => { const state = current.current; if (!state) return;
      await api(state.target.kind === "personal" ? `/whiteboards/personal/${board.id}?workspaceId=${state.id}&revision=${board.revision}` : `/whiteboards/workspaces/${state.id}/saved/${board.id}?revision=${board.revision}`, { method: "DELETE" }); await refresh(); await list(); },
    closeSolo: () => { setVisible(false); window.location.hash = ""; },
    switchSolo: (target) => { void transition(() => start(target)).catch((error) => setNotice(error.message)); },
    requestEnd: (work) => { void transition(work).catch((error) => setNotice(error.message)); },
    cancelEnd: () => { const action = continuation.current; continuation.current = null; setEnding(false); action?.resolve(false); }, finishEnd,
    serverNow: () => Date.now() + offset.current,
  } : sync;
  return <SoloContext.Provider value={{ visible, workspace, notice, start, restore, reset, transition }}><BoardContext.Provider value={{ ...value, personal, listPersonal,
    loadPersonal: async (id) => {
      if (soloActive) return load(id, true);
      const state = sync.snapshot; if (!state || !realtime.session) return;
      await api(`/sessions/${realtime.session.id}/whiteboard/personal/${id}/load`, { method: "POST", body: JSON.stringify({ generation: state.generation, revision: state.revision }) }); await sync.refresh();
    },
  }}>{children}</BoardContext.Provider></SoloContext.Provider>;
}
export function useSoloWhiteboard() { const value = useContext(SoloContext); if (!value) throw new Error("SoloWhiteboardProvider fehlt."); return value; }
